import cx from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { Fragment, useEffect, useRef } from 'react';

import Button from '../../button';
import MenuWithBack from '../../collapsible-list/menu-with-back';
import PartNameWithStock from '../../v2/categories/common/part-name-with-stock';

type TCategoryNavigation = {
  groups: TCategories;
  current: {
    category: Partial<TCategory>;
    part: TPart;
  } | null;
  onChange?: (category: TPart) => void;
  maxWidth?: boolean;
  maxHeight?: boolean;
  showBackButton?: boolean;
};

const CategoryNavigationLight = ({
  groups,
  current,
  onChange,
  maxWidth = false,
  maxHeight = true,
}: TCategoryNavigation) => {
  const { t } = useTranslation();
  const { locale = 'en' } = useRouter();

  const groupName = (id: string) => {
    switch (id) {
      case '1':
        return t('header:groups.group-1');
      case '2':
        return t('header:groups.group-2');
      case '3':
        return t('header:groups.group-3');
      default:
        return;
    }
  };
  const listItemRef = useRef(null);
  return (
    <div data-cy="parts-collapsible" className={cx('')}>
      <MenuWithBack
        items={groups.map((g, index) => ({
          header: (
            <div
              className={cx(
                'min-h-12 py-2 text-left flex flex-row justify-between items-center grow-0 text-wrap group'
              )}
            >
              <div
                className={cx(
                  {
                    'font-light text-base':
                      !current || current.category.slug !== g.slug,
                  },
                  'px-4 flex flex-wrap'
                )}
              >
                {g.name}
              </div>
              {g.total_products > 0 && (
                <div
                  className={cx(
                    'p-1 group-focus-within:bg-white group-hover:bg-white font-bold text-xs w-fit flex justify-self-end mr-4',
                    !!current && current.category.slug === g.slug
                      ? 'bg-white'
                      : 'bg-brand-10'
                  )}
                >
                  {g.total_products.toLocaleString(locale)}
                </div>
              )}
            </div>
          ),
          content: (
            <ul
              className={cx('pt-2', {
                'lg:max-w-[303px]': maxWidth,
              })}
            >
              {Object.entries(g.parts).map(([separator, pArray]) => {
                return (
                  <Fragment key={'group-' + separator}>
                    <li
                      className={cx(
                        'px-4 text-left flex flex-row justify-between items-center grow-0 text-wrap font-bold text-xs group',
                        {
                          'pt-4 ': parseInt(separator) > 0,
                        }
                      )}
                    >
                      {groupName(separator)}
                    </li>
                    {pArray.map((p) => {
                      return p.total_products > 0 ? (
                        <li key={p.id} className={cx('group')} ref={listItemRef}>
                          <Button
                            type="button"
                            onClick={(e) => onChange && onChange(p)}
                            disableHeight={true}
                            className={cx('!text-base', {
                              'bg-brand-10':
                                !!current && current.part.slug === p.slug,
                            })}
                          >
                            <PartNameWithStock
                              name={p.name}
                              slug={p.slug}
                              totalStock={p.total_products}
                              display="column"
                              inverted={true}
                              stockWithBg={true}
                              displayCheck={false}
                              isChecked={
                                !!current && current.part.slug === p.slug
                              }
                              isAllowedOnlyOnPro={p.is_pro_allowed}
                              isAllowed={p.is_allowed}
                            />
                          </Button>
                        </li>
                      ) : (
                        <li key={p.id} tabIndex={-1} className={cx('group')}>
                          <div
                            className={cx('px-4', {
                              'bg-brand-10':
                                !!current && current.part.slug === p.slug,
                            })}
                          >
                            <PartNameWithStock
                              name={p.name}
                              slug={p.slug}
                              totalStock={p.total_products}
                              display="column"
                              inverted={true}
                              stockWithBg={true}
                              displayCheck={false}
                              showStock={false}
                              isChecked={
                                !!current && current.part.slug === p.slug
                              }
                              isAllowedOnlyOnPro={p.is_pro_allowed}
                              isAllowed={p.is_allowed}
                            />
                          </div>
                        </li>
                      );
                    })}
                  </Fragment>
                );
              })}
            </ul>
          ),
          key: index,
          slug: g.slug,
          isSelectedSlug: current && current.category.slug === g.slug,
          categoryHasStock: g.total_products > 0,
        }))}
        withGap={false}
        currentSelection={current}
      />
    </div>
  );
};

export default CategoryNavigationLight;
